/**
 * External Auth Flow Helpers
 *
 * Centralized handling for external authentication flows (mobile apps, CLI tools).
 * These flows store callback URLs in sessionStorage and redirect after auth.
 *
 * Flow:
 * 1. External client opens /login with params (e.g., ?cli=true&cli_callback=...)
 * 2. Login.tsx stores params in sessionStorage (for OAuth flows that navigate away)
 * 3. User authenticates (password, Google, SSO, etc.)
 * 4. After auth, handleExternalAuthRedirect() checks sessionStorage/URL and redirects
 *
 * Special case: Already-authenticated users
 * - If user is already logged in when browser opens /login with params,
 *   Login.tsx never renders (App.tsx routes to dashboard instead)
 * - So we also check URL params directly, not just sessionStorage
 */

import { consumeMCPAuthorizationContinuation } from './mcpOAuthContinuation';
import {
    MOBILE_AUTH_SESSION_KEYS,
    clearMobileAuthSession,
    hasFreshMobileAuthSession,
    hasLegacyMobileAuthSessionWithoutTimestamp,
} from './mobileAuthSession';

export interface ExternalAuthDeps {
    apiHost: string;
    getPathname: () => string;
    getUrlSearch: () => string;
    getSessionItem: (key: string) => string | null;
    removeSessionItem: (key: string) => void;
    getNowMs: () => number;
    redirect: (url: string) => void;
}

const inferAppTypeFromRedirectUri = (mobileRedirectUri: string | null): string | null => {
    if (!mobileRedirectUri) {
        return null;
    }

    if (mobileRedirectUri.startsWith('resq-facility://')) {
        return 'facility';
    }

    if (mobileRedirectUri.startsWith('resq-partner://')) {
        return 'vendor';
    }

    return null;
};

const getApiHostFromWindow = (): string => {
    if (typeof window === 'undefined') {
        throw new Error('Required environment variable is not defined: API_HOST');
    }

    const env = (window as { ENV?: Record<string, string | undefined> }).ENV;
    const apiHost = env?.API_HOST;

    if (apiHost === undefined) {
        throw new Error('Required environment variable is not defined: API_HOST');
    }

    return apiHost;
};

const getDefaultDeps = (): ExternalAuthDeps => {
    const apiHost = getApiHostFromWindow();
    return {
        apiHost,
        getPathname: () => window.location.pathname,
        getUrlSearch: () => window.location.search,
        getSessionItem: (key) => sessionStorage.getItem(key),
        removeSessionItem: (key) => sessionStorage.removeItem(key),
        getNowMs: () => Date.now(),
        redirect: (url) => {
            window.location.href = url;
        },
    };
};

const LEGACY_MOBILE_SESSION_COMPAT_PATHS = new Set(['/', '/login', '/sso', '/auth/google']);

/**
 * Core logic for handling external auth redirects.
 * Exported for testing with injected dependencies.
 */
export const handleExternalAuthRedirectWithDeps = (deps: ExternalAuthDeps): boolean => {
    const pathname = deps.getPathname();

    // During explicit logout we must not trigger mobile/CLI callback redirects.
    if (pathname.startsWith('/sign-out')) {
        return false;
    }

    if (pathname !== '/mcp/authorize') {
        const mcpAuthorizationUrl = consumeMCPAuthorizationContinuation({
            search: deps.getUrlSearch(),
            apiHost: deps.apiHost,
            getItem: deps.getSessionItem,
            removeItem: deps.removeSessionItem,
            nowMs: deps.getNowMs(),
        });
        if (mcpAuthorizationUrl) {
            deps.redirect(mcpAuthorizationUrl);
            return true;
        }
    }

    // Check for CLI callback - first in sessionStorage, then in URL params
    // URL params cover the case where user is already authenticated (Login.tsx never rendered)
    const urlParams = new URLSearchParams(deps.getUrlSearch());
    const cliCallbackFromUrl = urlParams.get('cli_callback');
    const cliCallback = deps.getSessionItem('cli_callback') || cliCallbackFromUrl;

    if (cliCallback) {
        console.log('[CLI Login] Redirecting to CLI callback');
        deps.removeSessionItem('cli_callback');
        const params = new URLSearchParams({ callback: cliCallback });
        deps.redirect(`${deps.apiHost}/api/auth/cli-oauth-callback/?${params.toString()}`);
        return true;
    }

    const isMobileLoginUrl = urlParams.get('mobile') === 'true';
    const hasFreshMobileSession = hasFreshMobileAuthSession((key) => deps.getSessionItem(key));
    const hasLegacyMobileSession =
        hasLegacyMobileAuthSessionWithoutTimestamp((key) => deps.getSessionItem(key)) &&
        LEGACY_MOBILE_SESSION_COMPAT_PATHS.has(pathname);
    const hasStaleMobileSession =
        deps.getSessionItem(MOBILE_AUTH_SESSION_KEYS.login) === 'true' &&
        !hasFreshMobileSession &&
        !hasLegacyMobileSession;
    const isMobileFlow = isMobileLoginUrl || hasFreshMobileSession || hasLegacyMobileSession;

    if (!isMobileFlow) {
        if (hasStaleMobileSession) {
            clearMobileAuthSession((key) => deps.removeSessionItem(key));
        }
        return false;
    }

    // URL params are source of truth for current login attempt.
    const mobileRedirectUri =
        urlParams.get('app_redirect_uri') ||
        (hasFreshMobileSession || hasLegacyMobileSession
            ? deps.getSessionItem(MOBILE_AUTH_SESSION_KEYS.redirectUri)
            : null);

    if (mobileRedirectUri) {
        console.log('[Mobile Login] Redirecting to mobile callback');
        const appType =
            urlParams.get('app_type') ||
            inferAppTypeFromRedirectUri(urlParams.get('app_redirect_uri')) ||
            inferAppTypeFromRedirectUri(mobileRedirectUri) ||
            deps.getSessionItem(MOBILE_AUTH_SESSION_KEYS.appType) ||
            'facility';
        clearMobileAuthSession((key) => deps.removeSessionItem(key));
        const params = new URLSearchParams({
            app_redirect_uri: mobileRedirectUri,
            app_type: appType,
        });
        deps.redirect(`${deps.apiHost}/api/auth/mobile-oauth-callback/?${params.toString()}`);
        return true;
    }

    if (hasStaleMobileSession) {
        clearMobileAuthSession((key) => deps.removeSessionItem(key));
    }

    return false;
};

/**
 * Checks for external auth flows in sessionStorage or URL params and redirects if found.
 * Call this after successful authentication before doing any other redirects.
 *
 * Checks both sessionStorage (for OAuth flows) and current URL (for already-authenticated users).
 *
 * @returns true if an external auth redirect was triggered, false otherwise
 */
export const handleExternalAuthRedirect = (): boolean => {
    return handleExternalAuthRedirectWithDeps(getDefaultDeps());
};
